import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Search, Calendar, Calculator, Package, History, User, LogOut, Sparkles, Gift, ShoppingBag, Smartphone, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Check if running in standalone mode (installed as PWA)
const checkIsStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');
};

// Check device type
const checkDeviceType = () => {
  if (typeof window === 'undefined') return { isIOS: false, isAndroid: false };
  const userAgent = window.navigator.userAgent.toLowerCase();
  return {
    isIOS: /iphone|ipad|ipod/.test(userAgent) && !window.MSStream,
    isAndroid: /android/.test(userAgent)
  };
};

export default function NavigationMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { totalActions, lifetimeLimit, actionsRemaining } = useGuest();
  
  const isStandalone = checkIsStandalone();
  const deviceType = checkDeviceType();

  // Listen for install prompt
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  // Fetch subscription status
  useEffect(() => {
    if (user && token) {
      axios.get(`${API}/api/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => setSubscription(res.data.subscription))
      .catch(err => console.error('Failed to fetch subscription:', err));
    }
  }, [user, token]);

  // Handle install
  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
      }
      setDeferredPrompt(null);
      setShowInstallModal(false);
    }
  };

  // Get plan display name
  const getPlanName = () => {
    if (!subscription) return 'Free Plan';
    const tier = subscription.tier;
    if (tier === 'lifetime_pro') return 'Lifetime Pro';
    if (tier === 'pro') return 'Pro Plan';
    if (tier === 'basic') return 'Basic Plan';
    return 'Free Plan';
  };

  // Get plan color
  const getPlanColor = () => {
    if (!subscription) return '#6b7280';
    const tier = subscription.tier;
    if (tier === 'lifetime_pro') return '#f59e0b'; // amber
    if (tier === 'pro') return '#8b5cf6';
    if (tier === 'basic') return '#22c55e';
    return '#6b7280';
  };

  const menuItems = [
    { icon: Search, label: 'Search', path: '/dashboard' },
    { icon: Package, label: 'Personal Inventory', path: '/inventory' },
    { icon: Calculator, label: 'Profit Calculator', path: '/calculator' },
    { icon: Calendar, label: 'Release Schedule', path: '/releases' },
    { icon: History, label: 'Search History', path: '/history' },
    { icon: Gift, label: 'Refer & Earn', path: '/referral' },
    { icon: ShoppingBag, label: 'Merch', path: null, comingSoon: true },
  ];
  
  // Add Install App option if not already installed
  if (!isStandalone) {
    menuItems.push({ 
      icon: Smartphone, 
      label: 'Install App', 
      path: null, 
      isInstall: true 
    });
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleNavigate = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Menu Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
        aria-label="Open menu"
        aria-expanded={isOpen}
        data-testid="nav-menu-btn"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 w-72 rounded-xl overflow-hidden"
          style={{ 
            zIndex: 9999,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e5e7eb'
          }}
          data-testid="nav-dropdown"
        >
          {/* User Status Header */}
          <div 
            style={{ 
              backgroundColor: user ? '#dcfce7' : '#f3f4f6',
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb'
            }}
          >
            {user ? (
              // Logged in user
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div 
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <User style={{ width: '20px', height: '20px', color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: '600', fontSize: '14px', color: '#111827', margin: 0 }}>
                    {user.name || user.email}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                    <Sparkles style={{ width: '12px', height: '12px', color: getPlanColor() }} />
                    <span style={{ fontSize: '12px', color: getPlanColor(), fontWeight: '600' }}>{getPlanName()}</span>
                  </div>
                </div>
              </div>
            ) : (
              // Guest user
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div 
                    style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      backgroundColor: '#9ca3af',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <User style={{ width: '20px', height: '20px', color: 'white' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: '600', fontSize: '14px', color: '#111827', margin: 0 }}>
                      Guest Mode
                    </p>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                      {totalActions}/{lifetimeLimit} free searches used
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleNavigate('/auth')}
                  style={{
                    width: '100%',
                    marginTop: '10px',
                    padding: '8px 12px',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Sparkles style={{ width: '14px', height: '14px' }} />
                  Sign Up for Unlimited
                </button>
              </div>
            )}
          </div>

          {/* Navigation Items */}
          <div style={{ backgroundColor: '#ffffff', padding: '8px 0' }}>
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = item.path && location.pathname === item.path;
              const isComingSoon = item.comingSoon;
              const isInstall = item.isInstall;
              
              return (
                <button
                  key={item.path || `menu-${index}`}
                  type="button"
                  onClick={() => {
                    if (isComingSoon) {
                      toast.info('Merch store coming soon! 🛍️');
                    } else if (isInstall) {
                      setShowInstallModal(true);
                      setIsOpen(false);
                    } else {
                      handleNavigate(item.path);
                    }
                  }}
                  style={{ 
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    backgroundColor: isActive ? '#22c55e' : '#ffffff',
                    color: isActive ? '#ffffff' : isComingSoon ? '#9ca3af' : isInstall ? '#22c55e' : '#374151',
                    opacity: isComingSoon ? 0.8 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = '#ffffff';
                  }}
                  data-testid={`nav-item-${item.path ? item.path.replace('/', '') : item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                  <span style={{ fontWeight: '500', fontSize: '14px', flex: 1 }}>{item.label}</span>
                  {isComingSoon && (
                    <span style={{ 
                      fontSize: '10px', 
                      fontWeight: '600',
                      backgroundColor: '#fef3c7',
                      color: '#d97706',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      SOON
                    </span>
                  )}
                  {isInstall && (
                    <span style={{ 
                      fontSize: '10px', 
                      fontWeight: '600',
                      backgroundColor: '#dcfce7',
                      color: '#16a34a',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      FREE
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Logout button for logged in users */}
          {user && (
            <div 
              style={{ 
                backgroundColor: '#ffffff',
                borderTop: '1px solid #e5e7eb',
                padding: '8px 0'
              }}
            >
              <button
                onClick={handleLogout}
                style={{ 
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  backgroundColor: '#ffffff',
                  color: '#ef4444'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
              >
                <LogOut style={{ width: '20px', height: '20px', flexShrink: 0 }} />
                <span style={{ fontWeight: '500', fontSize: '14px' }}>Sign Out</span>
              </button>
            </div>
          )}

          {/* Footer */}
          <div 
            style={{ 
              backgroundColor: '#f3f4f6',
              padding: '8px 16px',
              borderTop: '1px solid #e5e7eb'
            }}
          >
            <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', margin: 0 }}>
              FlipValue v1.0
            </p>
          </div>
        </div>
      )}

      {/* Install App Modal */}
      {showInstallModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
          onClick={() => setShowInstallModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '360px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '12px', 
                  backgroundColor: '#22c55e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Smartphone style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontWeight: '700', fontSize: '18px', margin: 0, color: '#111827' }}>Install FlipValue</h3>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Add to your home screen</p>
                </div>
              </div>
              <button 
                onClick={() => setShowInstallModal(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px'
                }}
              >
                <X style={{ width: '20px', height: '20px', color: '#9ca3af' }} />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '12px' }}>Benefits of Installing:</h4>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#4b5563', fontSize: '14px', lineHeight: '1.8' }}>
                <li><strong>Instant Access</strong> – Launch directly from your home screen</li>
                <li><strong>Faster Loading</strong> – App loads quicker than browser</li>
                <li><strong>Full Screen</strong> – No browser bars, more space</li>
                <li><strong>Works Offline</strong> – Access cached data without internet</li>
                <li><strong>Native Feel</strong> – Looks and feels like a real app</li>
              </ul>
            </div>

            {deferredPrompt ? (
              <button
                onClick={handleInstall}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Smartphone style={{ width: '20px', height: '20px' }} />
                Install Now
              </button>
            ) : (
              <div style={{ 
                backgroundColor: '#f3f4f6', 
                borderRadius: '12px', 
                padding: '16px',
                fontSize: '14px',
                color: '#4b5563'
              }}>
                {deviceType.isIOS ? (
                  <div>
                    <p style={{ fontWeight: '600', marginBottom: '8px', color: '#111827' }}>To install on iPhone/iPad:</p>
                    <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>Tap the <strong>Share</strong> button (box with arrow)</li>
                      <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                      <li>Tap <strong>"Add"</strong> to confirm</li>
                    </ol>
                  </div>
                ) : deviceType.isAndroid ? (
                  <div>
                    <p style={{ fontWeight: '600', marginBottom: '8px', color: '#111827' }}>To install on Android:</p>
                    <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>Tap the <strong>menu</strong> (3 dots) in your browser</li>
                      <li>Tap <strong>"Add to Home screen"</strong></li>
                      <li>Tap <strong>"Add"</strong> to confirm</li>
                    </ol>
                  </div>
                ) : (
                  <div>
                    <p style={{ fontWeight: '600', marginBottom: '8px', color: '#111827' }}>To install on Desktop:</p>
                    <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>Click the <strong>install icon</strong> in the address bar</li>
                      <li>Or use browser menu → <strong>"Install FlipValue"</strong></li>
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
